import { type ChangeEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getRegister, listProducts } from '../../api/stock'
import { getMyPractices } from '../../api/staff'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { inputClass, selectClass } from '../../components/ui/FormField'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass } from '@/lib/tableStyles'
import type { StockMovementType, StockRegisterRow } from '../../types/stock'

const MOVEMENT_COLORS: Record<StockMovementType, { bg: string; color: string }> = {
  receive:   { bg: 'var(--success-bg)', color: 'var(--success-text)' },
  dispense:  { bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
  adjust:    { bg: 'var(--surface-alt)', color: 'var(--text-2)' },
  write_off: { bg: 'var(--danger-bg)', color: 'var(--danger-text)' },
}

const MOVEMENT_LABELS: Record<StockMovementType, string> = {
  receive:   'Receive',
  dispense:  'Dispense',
  adjust:    'Adjust',
  write_off: 'Write-off',
}

function MovementBadge({ type }: { type: StockMovementType }) {
  const c = MOVEMENT_COLORS[type] ?? { bg: 'var(--surface-alt)', color: 'var(--text-3)' }
  return (
    <span
      className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
      style={{ background: c.bg, color: c.color }}
    >
      {MOVEMENT_LABELS[type] ?? type}
    </span>
  )
}

function ScheduleBadge({ schedule }: { schedule: number | null | undefined }) {
  const scheduled = schedule != null
  return (
    <span className={`inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold ${scheduled ? 'bg-accent-muted text-accent' : 'bg-surface-alt text-text-3'}`}>
      {scheduled ? `Schedule ${schedule}` : 'Unscheduled'}
    </span>
  )
}

function SigThumb({ data }: { data: string }) {
  return (
    <img
      src={data}
      alt="Signature"
      className="max-w-[80px] max-h-[30px] object-contain block"
    />
  )
}

export default function DrugRegister() {
  const { productId: productIdParam } = useParams<{ productId?: string }>()

  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    productIdParam ? Number(productIdParam) : null,
  )
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedPracticeId, setSelectedPracticeId] = useState<number | null>(null)

  const { data: practices, loading: practicesLoading, error: practicesError } = useFetch(
    () => getMyPractices(),
    [],
  )

  useEffect(() => {
    if (practices && practices.length > 0 && !selectedPracticeId) {
      setSelectedPracticeId(practices[0].id)
    }
  }, [practices, selectedPracticeId])

  const practiceId = selectedPracticeId

  const { data: products, loading: productsLoading } = useFetch(
    () => {
      if (!practiceId) return Promise.resolve([])
      return listProducts({ practice_id: practiceId })
    },
    [practiceId],
  )

  useEffect(() => {
    if (productIdParam) setSelectedProductId(Number(productIdParam))
  }, [productIdParam])

  const selectedProduct = products?.find((p) => p.id === selectedProductId) ?? null

  const { data: rows, loading: registerLoading, error: registerError } = useFetch(
    () => {
      if (!selectedProductId || !practiceId) return Promise.resolve([] as StockRegisterRow[])
      return getRegister(selectedProductId, {
        practice_id: practiceId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      })
    },
    [selectedProductId, practiceId, dateFrom, dateTo],
  )

  const lastRow = rows && rows.length > 0 ? rows[rows.length - 1] : null

  if (practicesLoading) return <div className="text-text-2 p-8">Loading…</div>
  if (practicesError) return <div className="p-8"><ErrorBox>{practicesError}</ErrorBox></div>
  if (!practices || practices.length === 0) {
    return <div className="p-8"><ErrorBox>No practices found.</ErrorBox></div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Drug Register</h1>
        <Button variant="outline" size="md" onClick={() => window.print()}>Print</Button>
      </div>

      <Card className="mb-5 p-4">
        <div className="flex gap-3 flex-wrap items-end">
          {practices.length > 1 && (
            <div>
              <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">Practice</label>
              <select
                className={`${selectClass()} w-[200px]`}
                value={selectedPracticeId ?? ''}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  setSelectedPracticeId(Number(e.target.value))
                  setSelectedProductId(null)
                }}
              >
                {practices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">Product</label>
            <select
              className={`${selectClass()} w-[260px]`}
              value={selectedProductId ?? ''}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedProductId(e.target.value ? Number(e.target.value) : null)}
              disabled={productsLoading}
            >
              <option value="">{productsLoading ? 'Loading…' : 'Select product…'}</option>
              {products?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">From</label>
            <input
              type="date"
              className={`${inputClass()} w-[155px]`}
              value={dateFrom}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">To</label>
            <input
              type="date"
              className={`${inputClass()} w-[155px]`}
              value={dateTo}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {selectedProduct && (
        <Card className="mb-5 p-4">
          <div className="flex gap-6 flex-wrap items-center">
            <div>
              <div className="text-xs text-text-3 font-semibold uppercase tracking-[0.05em]">Product</div>
              <div className="text-base font-bold text-text-heading">{selectedProduct.name}</div>
            </div>
            <div>
              <div className="text-xs text-text-3 font-semibold uppercase tracking-[0.05em]">Schedule</div>
              <div className="mt-[0.2rem]"><ScheduleBadge schedule={selectedProduct.schedule} /></div>
            </div>
            <div>
              <div className="text-xs text-text-3 font-semibold uppercase tracking-[0.05em]">Unit</div>
              <div className="text-[0.95rem] text-text">{selectedProduct.unit}</div>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <SectionTitle>Register Entries</SectionTitle>

        {registerError && <ErrorBox>{registerError}</ErrorBox>}

        {!selectedProductId ? (
          <div className="text-text-3 py-6 text-center">Select a product to view the register.</div>
        ) : registerLoading ? (
          <div className="text-text-3 py-6">Loading register…</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Patient</th>
                    <th className={thClass}>Client</th>
                    <th className={thClass}>Issued By</th>
                    <th className={thClass}>Checked By</th>
                    <th className={thClass}>Type</th>
                    <th className={`${thClass} text-right`}>Qty</th>
                    <th className={`${thClass} text-right`}>Balance</th>
                  </tr>
                </thead>
                <tbody className={tbodyHoverClass}>
                  {rows && rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`${tdClass} text-text-3 text-center py-8`}>
                        No entries found for the selected period.
                      </td>
                    </tr>
                  ) : (
                    rows?.map((row) => (
                      <tr key={row.id}>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {new Date(row.movement_date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className={tdClass}>{row.patient_name ?? <span className="text-text-3">—</span>}</td>
                        <td className={tdClass}>{row.client_name ?? <span className="text-text-3">—</span>}</td>
                        <td className={tdClass}>
                          <div>{row.issued_by_name ?? <span className="text-text-3">—</span>}</div>
                          {row.issued_by_signature && <SigThumb data={row.issued_by_signature} />}
                        </td>
                        <td className={tdClass}>
                          <div>{row.checked_by_name ?? <span className="text-text-3">—</span>}</div>
                          {row.checked_by_signature && <SigThumb data={row.checked_by_signature} />}
                        </td>
                        <td className={tdClass}><MovementBadge type={row.movement_type} /></td>
                        <td className={`${tdClass} text-right font-semibold`}>{row.quantity}</td>
                        <td className={`${tdClass} text-right font-mono`}>{row.balance_after}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {lastRow && (
              <div className="mt-5 pt-4 border-t-2 border-border flex justify-end items-center gap-4">
                <span className="text-sm text-text-3 font-semibold uppercase tracking-[0.05em]">
                  Current Balance
                </span>
                <span className="text-2xl font-bold text-accent font-mono">
                  {lastRow.balance_after}
                </span>
                {selectedProduct && (
                  <span className="text-sm text-text-3">{selectedProduct.unit}</span>
                )}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
